import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiClient, type ApiClientOptions } from './client';

const ApiClientContext = createContext<ApiClient | null>(null);

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          // Never retry auth/permission/validation failures — only transient ones.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Wires the ApiClient + React Query for a portal. The client is created once
 * (useState initializer) so interceptors and the in-memory token survive
 * re-renders.
 */
export function ApiProvider({
  options,
  children,
  queryClient,
}: {
  options: ApiClientOptions;
  children: ReactNode;
  queryClient?: QueryClient;
}) {
  const [client] = useState(() => new ApiClient(options));
  const [qc] = useState(() => queryClient ?? createQueryClient());
  const value = useMemo(() => client, [client]);
  return (
    <ApiClientContext.Provider value={value}>
      <QueryClientProvider client={qc}>{children}</QueryClientProvider>
    </ApiClientContext.Provider>
  );
}

export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) throw new Error('useApiClient must be used within <ApiProvider>');
  return client;
}
